/**
 * How screens read from the server: the one way. `useApi<TodayResponse>('/api/today')` sends the sign-in token,
 * parses JSON, and gives back { data, error, loading, reload }; hand it straight to <Screen query={…}>. Pass `null`
 * to wait (a drawer with nothing open). Every /api route is behind the access guard, so always go through this.
 *
 * Its insides will move to TanStack Query when the first screen that writes arrives; screens won't change.
 *
 * If the server answers 401 (the sign-in ran out mid-session), it refreshes the token once, silently, and asks
 * again. If that fails, the session is marked ended and the Gate asks the person to sign in again.
 */
import { useCallback, useEffect, useState } from 'react'
import { useSession, type Session } from './session'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message)
  }
}

export const SESSION_ENDED = 'session_ended'

/** What apiGet needs from the session. Without `refresh`, a 401 is returned as an error as it is. */
export type ApiAuth = Pick<Session, 'getToken'> & Partial<Pick<Session, 'refresh' | 'end'>>

async function send(path: string, auth: ApiAuth, body?: unknown): Promise<Response> {
  const token = await auth.getToken().catch(() => undefined)
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
  if (body === undefined) return fetch(path, { headers })
  return fetch(path, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
}

export async function apiGet<T>(path: string, auth: ApiAuth): Promise<T> {
  return request<T>(path, auth)
}

/** A POST with a JSON body, with the same sign-in, refresh and errors as apiGet. For small writes (a Learn tick). */
export async function apiPost<T>(path: string, body: unknown, auth: ApiAuth): Promise<T> {
  return request<T>(path, auth, body)
}

/**
 * A GET to a public address, with no sign-in token: the live app's /healthz, read from the preview by the Learn tab's
 * "Ship it" check. Never for anything behind sign-in.
 */
export async function publicGet<T>(url: string): Promise<T> {
  const res = await fetch(url, { cache: 'no-store' })
  const body = (await res.json().catch(() => null)) as (T & { error?: string }) | null
  if (!res.ok || body === null) throw new ApiError(res.status, body?.error ?? `${url} answered ${res.status}`)
  return body as T
}

async function request<T>(path: string, auth: ApiAuth, payload?: unknown): Promise<T> {
  let res = await send(path, auth, payload)
  if (res.status === 401 && auth.refresh) {
    // One refresh and one retry per request: never a loop.
    if (await auth.refresh()) res = await send(path, auth, payload)
    if (res.status === 401) {
      auth.end?.()
      throw new ApiError(401, 'Your session ended. Sign in again to carry on.', SESSION_ENDED)
    }
  }
  const body = (await res.json().catch(() => null)) as (T & { error?: string; code?: string }) | null
  if (!res.ok) throw new ApiError(res.status, body?.error ?? `The server answered ${res.status}`, body?.code)
  return body as T
}

export interface ApiQuery<T> {
  data?: T
  error?: ApiError
  loading: boolean
  reload: () => void
}

export function useApi<T>(path: string | null): ApiQuery<T> {
  const { getToken, refresh, end } = useSession()
  // Answers are kept with the path they belong to, so switching paths never shows the last path's data.
  const [state, setState] = useState<{ path: string | null; data?: T; error?: ApiError; loading: boolean }>({ path, loading: path !== null })
  const [nonce, setNonce] = useState(0)
  useEffect(() => {
    if (path === null) {
      setState({ path, loading: false })
      return
    }
    let live = true
    setState((s) => (s.path === path ? { ...s, loading: true } : { path, loading: true }))
    apiGet<T>(path, { getToken, refresh, end }).then(
      (data) => live && setState({ path, data, loading: false }),
      (error: unknown) =>
        live &&
        setState((s) => ({
          ...(s.path === path ? s : { path }),
          loading: false,
          error: error instanceof ApiError ? error : new ApiError(0, String(error)),
        })),
    )
    return () => {
      live = false
    }
  }, [path, getToken, refresh, end, nonce])
  const reload = useCallback(() => setNonce((n) => n + 1), [])
  if (state.path !== path) return { loading: path !== null, reload }
  return { data: state.data, error: state.error, loading: state.loading, reload }
}
